import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Search, Power, ShieldAlert, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AutoTradingTab = ({ token }) => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [killConfirm, setKillConfirm] = useState(false);
  const [killing, setKilling] = useState(false);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/mnro/auto-trading/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(res.data);
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [token]); // eslint-disable-line

  const toggleOne = async (userId, current) => {
    const next = !current;
    // Optimistic update
    setOverview((p) => p.map((u) => (u.id === userId ? { ...u, auto_trading_enabled: next } : u)));
    try {
      await axios.put(
        `${API}/mnro/users/${userId}/auto-trading`,
        { enabled: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Авто-трейдинг ${next ? "включён" : "остановлен"}`);
    } catch (e) {
      // Revert on error
      setOverview((p) => p.map((u) => (u.id === userId ? { ...u, auto_trading_enabled: current } : u)));
      toast.error(e.response?.data?.detail || "Ошибка");
    }
  };

  const stopAll = async () => {
    setKilling(true);
    try {
      const res = await axios.post(
        `${API}/mnro/auto-trading/stop-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Остановлено: ${res.data.stopped_users} пользователей`);
      setKillConfirm(false);
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка");
    } finally {
      setKilling(false);
    }
  };

  const filtered = overview.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.client_id?.toLowerCase().includes(q) ||
      u.id?.toLowerCase().includes(q)
    );
  });

  const activeCount = overview.filter((u) => u.auto_trading_enabled).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-chivo font-bold text-xl text-white flex items-center gap-2">
            <Bot size={20} className="text-[#EDEDED]" />
            Авто-трейдинг
          </h2>
          <p className="text-[#A1A1AA] text-sm mt-1">
            После закрытия сделки автоматически открывает новую на весь доступный баланс. 4 из 5 сделок в плюс.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[#A1A1AA] text-sm">
            Активно: <span className="text-[#34D399] font-bold">{activeCount}</span> / {overview.length}
          </div>
          <button
            onClick={() => setKillConfirm(true)}
            disabled={activeCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#F87171] hover:bg-[#E0342B] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded transition-colors"
            data-testid="kill-switch-btn"
          >
            <Power size={16} /> Остановить ВСЁ
          </button>
        </div>
      </div>

      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по ID, имени или email..."
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-3 py-2.5 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              data-testid="autotrading-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#A1A1AA]">Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[#A1A1AA]">Ничего не найдено</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Клиент</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Доступно</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">В работе</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Авто-сделок</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold px-4 py-3">Статус</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-[#1F1F1F] hover:bg-[#1A1A1A]/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-white/20 text-[#EDEDED] flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-white text-sm font-medium truncate">{u.name}</div>
                          <div className="text-[#71717A] text-xs truncate">{u.email}</div>
                          {u.client_id && <span className="text-[10px] text-[#EDEDED]">#{u.client_id}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white text-sm">
                      $ {Number(u.balance || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[#A1A1AA] text-sm">
                      $ {Number(u.trading_balance || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white text-sm">
                      {u.auto_trade_counter}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleOne(u.id, u.auto_trading_enabled)}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          u.auto_trading_enabled ? "bg-[#34D399]" : "bg-[#1F1F1F]"
                        }`}
                        data-testid={`autotrading-toggle-${u.id}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                            u.auto_trading_enabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Kill switch confirmation */}
      {killConfirm && (
        <div
          className="fixed inset-0 z-[90] bg-black/80-sm flex items-center justify-center p-4"
          onClick={() => !killing && setKillConfirm(false)}
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border border-[#F87171]/40 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#1F1F1F] flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <ShieldAlert size={20} className="text-[#F87171]" />
                Остановить ВСЕ
              </h3>
              <button onClick={() => !killing && setKillConfirm(false)} className="text-[#A1A1AA] hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-[#A1A1AA] text-sm mb-4">
                Это отключит авто-трейдинг у{" "}
                <span className="text-white font-bold">{activeCount}</span> пользователей.
                Открытые сейчас сделки продолжат работать до своего закрытия, но новые открываться НЕ будут.
              </p>
              <p className="text-[#F87171] text-sm mb-5">
                Действие нельзя отменить (нужно будет включать каждому вручную).
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setKillConfirm(false)}
                  disabled={killing}
                  className="flex-1 py-2.5 bg-[#1F1F1F] hover:bg-[#3F3F46] text-white rounded transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={stopAll}
                  disabled={killing}
                  className="flex-1 py-2.5 bg-[#F87171] hover:bg-[#E0342B] disabled:opacity-50 text-white font-semibold rounded transition-colors"
                  data-testid="kill-switch-confirm-btn"
                >
                  {killing ? "Остановка..." : "ОСТАНОВИТЬ ВСЁ"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoTradingTab;
